import React from "react";

import { Box, Chip, Icon, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";

import { LoadingComp } from "../../../shared/components/loading/Loading";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { IListagemPermissao } from "../../../shared/services/api/permissions/PermissionService";
import { formatDateBR } from "../../../shared/utils/format-date";

const profileLabel = (raw: unknown, profileLabelById?: Record<string, string>) => {
	const tryMap = (value: unknown) => {
		const v = String(value ?? "").trim();
		const key = v.toLowerCase();
		if (!key) return null;
		if (profileLabelById && profileLabelById[key]) return profileLabelById[key];
		return null;
	};

	if (raw && typeof raw === "object") {
		const obj = raw as any;
		return (
			tryMap(obj.clientTypeId) ||
			tryMap(obj.id) ||
			(String(obj.description ?? "").trim() || "-")
		);
	}

	const v = String(raw ?? "").trim();
	const key = v.toLowerCase();

	return (
		tryMap(v) ||
		(key === "admin" ? "Administrativo" :
		key === "estate" ? "Imobiliária" :
		key === "broker" ? "Parceiro" :
		(v || "-"))
	);
};

type TStatus = "active" | "inactive";

const normalizeStatus = (raw: unknown): TStatus => {
	const v = String(raw ?? "").toLowerCase();
	if (v === "inactive" || v === "0" || v.includes("inativ")) return "inactive";
	return "active";
};

const statusChipSx = (status: TStatus) => {
	if (status === "inactive") {
		return {
			color: "#e40909",
			fontWeight: 500,
			background: "#fce4e4",
			border: "0.5px solid #f1f1f1",
		} as const;
	}

	return {
		color: "#0f8a0d",
		fontWeight: 500,
		background: "#d9f8e5",
		border: "0.5px solid #f1f1f1",
	} as const;
};

interface TablePermissionsProps {
	rows: IListagemPermissao[];
	isLoading: boolean;
	totalCount: number;
	pagina: number;
	rowsPerPage: number;
	profileLabelById?: Record<string, string>;
	onChangePage: (newPage: number) => void;
	onChangeRowsPerPage: (newRowsPerPage: number) => void;
	onEdit: (permissionId: string) => void;
}

export const TablePermissions: React.FC<TablePermissionsProps> = ({ rows, isLoading, totalCount, pagina, rowsPerPage, profileLabelById, onChangePage, onChangeRowsPerPage, onEdit }) => {
	const canEditPermission = checkByProfileGuard(PermissionRolesEnum.PermissionsOptions, PermissionActionsEnum.Update);

	return (
		<TableContainer
			component={Paper}
			variant="elevation"
			sx={{
				mt: 1,
				width: "96%",
				px: { xs: 1, sm: 3 },
				py: { xs: 2, sm: 3 },
				ml: 1,
				borderRadius: 2,
				overflow: "hidden",
			}}
		>
			<Table size="small" stickyHeader aria-label="tabela de permissões">
				<TableHead>
					<TableRow sx={{ backgroundColor: "grey.100" }}>
						<TableCell>
							<Typography variant="button">Descrição</Typography>
						</TableCell>
						<TableCell>
							<Typography variant="button">Perfil</Typography>
						</TableCell>
							<TableCell>
								<Typography variant="button">Criado em</Typography>
							</TableCell>
						<TableCell>
							<Typography variant="button">Status</Typography>
						</TableCell>
						<TableCell align="right">
							<Typography variant="button">Ações</Typography>
						</TableCell>
					</TableRow>
				</TableHead>

				<TableBody>
					{rows.length === 0 && !isLoading && (
						<TableRow>
							<TableCell colSpan={5} align="center">
								Nenhum registro
							</TableCell>
						</TableRow>
					)}

					{rows.map((row) => (
						<TableRow key={row.id} hover>
							<TableCell>
								{row.description}
							</TableCell>

							<TableCell>
								<Typography variant="body2" color="#878b8b">
									{profileLabel((row as any).clientTypeId, profileLabelById)}
								</Typography>
							</TableCell>

							<TableCell>
								<Typography variant="body2" color="#878b8b">
									{(() => {
										const raw = (row as any).created_at ?? (row as any).createdAt;
										return raw ? formatDateBR(raw, true) : "-";
									})()}
								</Typography>
							</TableCell>

							<TableCell>
								{(() => {
									const derivedStatus = normalizeStatus((row as any).status);
									return <Chip size="medium" label={derivedStatus === "inactive" ? "Inativo" : "Ativo"} sx={statusChipSx(derivedStatus)} />;
								})()}
							</TableCell>

							<TableCell align="right">
								<IconButton size="small" onClick={() => onEdit(row.id)} disabled={!canEditPermission}>
									<Icon sx={{ color: canEditPermission ? "#0170B3" : "action.disabled" }}>app_registration</Icon>
								</IconButton>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>

			<LoadingComp isopen={isLoading} />

			<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
				<TablePagination
					component="div"
					count={totalCount}
					page={Math.max(pagina - 1, 0)}
					rowsPerPage={rowsPerPage}
					onPageChange={(_, newPage) => onChangePage(newPage + 1)}
					onRowsPerPageChange={(e) => onChangeRowsPerPage(parseInt(e.target.value, 10))}
					rowsPerPageOptions={[10, 25, 50]}
					labelRowsPerPage="Linhas por página:"
					labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
				/>
			</Box>
		</TableContainer>
	);
};
